/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import { getWindowResolution } from "../utils/system";
import { Keyboard } from "../input/GuacamoleKeyboard";
import {
  GamepadPollingManager,
  type GamepadButtonEvent,
  type GamepadAxisEvent,
  type GamepadConnectionEvent,
} from "@onmo/gamepad-manager";
import { MouseState } from "../input/MouseState";
import { TouchState } from "../input/TouchState";
import { ScrollState } from "../input/ScrollState";
import {
  CoordinateMapper,
  type CoordinateMapping,
} from "../input/CoordinateMapper";
import { EventManager } from "../input/EventManager";
import { ResizeManager } from "../input/ResizeManager";
import { HotkeyDetector } from "../input/HotkeyDetector";
import { DeviceDetector } from "../input/DeviceDetector";
import {
  MouseSimulationMode,
  MouseSimulator,
  type SyntheticMouseEvent,
} from "../input/MouseSimulator";
import { EON_KEYBOARD_INPUT_ID, type StreamStatus } from "..";
import {
  exitPointerLock,
  requestKeyboardLock,
  requestKeyboardUnlock,
  requestPointerLock,
} from "../utils/screen";

// ==================== TYPE DEFINITIONS ====================

interface ClientResolution {
  width: number;
  height: number;
}

interface InputCallbacks {
  onMenuHotkey: (() => void) | null;
  onFullscreenHotkey: (() => void) | null;
  onFullscreenChange: (() => void) | null;
  onGamepadConnected: ((gamepadId: string) => void) | null;
  onGamepadDisconnected: (() => void) | null;
  onResizeEnd: (() => void) | null;
  onGamepadPollingStart?: () => void;
  onGamepadPollingStop?: () => void;
}

interface InputFeatures {
  relativeMouseMode: boolean;
  cursorScaling: number | null;
  canLockInputs: boolean;
}

interface CursorScalingOptions {
  remoteResolutionEnabled?: boolean;
}

// ==================== MAIN INPUT CLASS ====================

/**
 * WebRTC Input Handler
 * Manages all input events (mouse, touch, keyboard, gamepad) for remote desktop applications
 */
export class InputHandler {
  private readonly videoElement: HTMLVideoElement;
  private readonly sendMessage: (message: string) => void;

  // Input state
  private readonly mouseState: MouseState;
  private readonly touchState: TouchState;
  private readonly scrollState: ScrollState;
  private readonly coordinateMapper: CoordinateMapper;

  // Input devices
  private keyboardHandler: Keyboard | null = null;
  private gamepadHandler: { listenerIds: string[] } | null = null;

  // Mouse simulation for gamepad
  private mouseSimulator: MouseSimulator;

  // Event callbacks
  private readonly callbacks: InputCallbacks;

  // Event listener management
  private readonly eventManager: EventManager;
  private readonly resizeManager: ResizeManager;

  // Feature flags
  private readonly features: InputFeatures;
  private isAttached = false;
  private shouldPoll = true;
  private isMenuOpen = false;
  // True when using the keyboardInput fallback (TV native keyboard).
  // In this mode, navigation keys (arrows, Enter, …) still arrive via window
  // keydown and must be forwarded manually since Guacamole is not on window.
  private useNativeKeyboardInput = false;
  private readonly isTV: boolean;

  constructor(
    videoElement: HTMLVideoElement,
    sendFunction: (message: string) => void,
    { isTV = false }: { isTV?: boolean } = {},
  ) {
    this.videoElement = videoElement;
    this.sendMessage = sendFunction;

    // Input state
    this.mouseState = new MouseState();
    this.touchState = new TouchState();
    this.scrollState = new ScrollState();
    this.coordinateMapper = new CoordinateMapper(videoElement);

    // Mouse simulation for gamepad
    this.mouseSimulator = new MouseSimulator(undefined, isTV);
    this.mouseSimulator.setMode(MouseSimulationMode.GESTURE);
    this.mouseSimulator.onSyntheticMouseMove = (movX, movY, clientX, clientY) =>
      this.handleSyntheticMouseMove(movX, movY, clientX, clientY);
    this.mouseSimulator.onGestureStart = () => this.enableRemotePointer();

    // Event callbacks
    this.callbacks = {
      onMenuHotkey: null,
      onFullscreenHotkey: () => this.enterFullscreen(),
      onFullscreenChange: null,
      onResizeEnd: () => this.onResizeEnd(),
      onGamepadConnected: null,
      onGamepadDisconnected: null,
    };

    // Event listener management
    this.eventManager = new EventManager();
    this.resizeManager = new ResizeManager(() =>
      this.callbacks.onResizeEnd?.(),
    );

    // Feature flags
    this.features = {
      relativeMouseMode: false,
      cursorScaling: null,
      canLockInputs: false,
    };

    this.isTV = isTV;
    this.handleFullscreenChange();
    this.setupGamepadHandler();
  }

  // ==================== PUBLIC API ====================

  /**
   * Set stream status for mouse simulation
   */
  setStreamStatus(streamStatus: StreamStatus | undefined): void {
    const wasSimulating = this.mouseSimulator.shouldSimulateMouseEvents();
    this.mouseSimulator.setStreamStatus(streamStatus);
    const isSimulating = this.mouseSimulator.shouldSimulateMouseEvents();

    // On TV there is no pointer-lock transition to drive remote cursor visibility
    if (this.isTV && wasSimulating !== isSimulating) {
      if (isSimulating) {
        this.enableRemotePointer();
      } else {
        this.hideRemotePointer();
      }
    }
  }

  setShouldPoll(shouldPoll: boolean): void {
    this.shouldPoll = shouldPoll;
  }

  setMenuOpen(isMenuOpen: boolean): void {
    this.isMenuOpen = isMenuOpen;
  }

  /**
   * Forward a single key press/release to the remote. Accepts a DOM key string
   * (e.g. "ArrowLeft"); special keys map via Guacamole's SPECIAL_KEYS, printable
   * single characters fall back to their char code. Used by the on-screen
   * keyboard for keys that don't change the input value (arrows, etc.).
   */
  sendKey(key: string): void {
    if (key.length === 1) {
      this.keyboardHandler?.type(key);
      return;
    }

    const keysym = Keyboard.SPECIAL_KEYS[key]?.[0];
    if (keysym == null) return;
    this.keyboardHandler?.press(keysym);
    this.keyboardHandler?.release(keysym);
  }

  updateMapping() {
    this.coordinateMapper.updateMapping();
    this.mouseSimulator.updateVideoRect();
    this.resizeManager.handleResizeStart();
  }

  /**
   * Get the mouse simulator instance to configure mode and settings
   */
  getMouseSimulator(): MouseSimulator {
    return this.mouseSimulator;
  }

  /**
   * Enable/disable debug timing logs for gamepad → mouse simulation pipeline.
   * Shows poll intervals, activation lag, dispatch intervals, and phase lag.
   * Usage: connection.inputHandler.setDebugMode(true)
   */
  setDebugMode(enabled: boolean): void {
    this.mouseSimulator.debug = enabled;
    if (this.gamepadHandler) {
      GamepadPollingManager.getInstance().updateConfig({ debug: enabled });
    }
  }

  /**
   * Enable/disable relative mouse mode (pointer lock)
   */
  setRelativeMouseMode(enabled: boolean): void {
    this.features.relativeMouseMode = enabled;
  }

  /**
   * Enable pointer lock and keyboard lock requests once input channel is open.
   */
  enableInputLocking(): void {
    this.features.canLockInputs = true;

    if (document.fullscreenElement) {
      if (!document.pointerLockElement) {
        this.requestPointerLock();
      } else if (!DeviceDetector.isTouchDevice()) {
        this.handlePointerLockChange();
      }
      this.requestKeyboardLock();
    }
  }

  /**
   * Calculate cursor scaling factor when client and server have different resolutions
   */
  calculateCursorScaling({
    remoteResolutionEnabled = false,
  }: CursorScalingOptions = {}): void {
    if (remoteResolutionEnabled) {
      this.features.cursorScaling = null;
      return;
    }

    const clientRes = this.getClientResolution();
    const serverWidth = this.videoElement.videoWidth;
    const serverHeight = this.videoElement.videoHeight;

    if (isNaN(serverWidth) || isNaN(serverHeight)) {
      console.log("Invalid video dimensions");
      return;
    }

    // Skip scaling if resolutions are similar
    const widthDiff = Math.abs(clientRes.width - serverWidth);
    const heightDiff = Math.abs(clientRes.height - serverHeight);

    if (widthDiff <= 10 && heightDiff <= 10) {
      this.features.cursorScaling = null;
      return;
    }

    // Calculate diagonal-based scaling factor
    const serverDiagonal = Math.sqrt(serverWidth ** 2 + serverHeight ** 2);
    const clientDiagonal = Math.sqrt(
      clientRes.width ** 2 + clientRes.height ** 2,
    );
    this.features.cursorScaling = serverDiagonal / clientDiagonal;
  }

  /**
   * Enter fullscreen mode with pointer lock
   */
  enterFullscreen(): void {
    if (!document.pointerLockElement) {
      this.requestPointerLock();
    }

    if (!document.fullscreenElement) {
      document.documentElement
        ?.requestFullscreen()
        .then(() => console.log("Fullscreen activated"))
        .catch((error: Error) => console.log("Fullscreen failed:", error));
    }
  }

  onResizeEnd(): void {
    const [width, height] = getWindowResolution();
    const newResolution = `${width}x${height}`;

    console.log(`Window size changed, scaled to: ${newResolution}`);
    this.sendMessage(`r,${newResolution}`);
    this.sendMessage(`s,${window.devicePixelRatio}`);
  }

  /**
   * Start listening for input events
   */
  attach(): void {
    if (this.isAttached) {
      console.warn("InputHandler already attached, skipping duplicate attach");
      return;
    }
    this.isAttached = true;
    this.setupGlobalEventListeners();
    this.attachInputContext();
    console.log("InputHandler attached");
  }

  /**
   * Stop listening for input events
   */
  detach(): void {
    this.isAttached = false;
    this.eventManager.removeAllListeners();
    this.detachInputContext();

    // Remove all gamepad listeners
    if (this.gamepadHandler) {
      const manager = GamepadPollingManager.getInstance();
      this.gamepadHandler.listenerIds.forEach((id) =>
        manager.removeEventListener(id),
      );
      this.gamepadHandler = null;
    }

    // Cleanup mouse simulator
    this.mouseSimulator.destroy();
  }

  /**
   * Attach input-specific event listeners
   */
  attachInputContext(): void {
    this.setupScrollListeners();
    this.setupKeyboardListeners();
    this.setupPointerInputListeners();
    this.initializeKeyboardHandler();
    this.coordinateMapper.updateMapping();
    this.mouseSimulator.updateVideoRect();
  }

  /**
   * Remove input-specific event listeners
   */
  detachInputContext(): void {
    this.eventManager.removeContextListeners();
    this.cleanupKeyboardHandler();
    this.exitPointerLock();
    requestKeyboardUnlock();
  }

  // ==================== EVENT SETUP ====================

  private setupGlobalEventListeners(): void {
    const { addListener } = this.eventManager;

    // Window and video events
    /*  addListener(this.videoElement, "resize", () =>
      this.coordinateMapper.updateMapping(),
    );
    addListener(window, "resize", () => this.coordinateMapper.updateMapping());
    addListener(window, "resize", () => this.resizeManager.handleResizeStart()); */
    addListener(window, "scroll", () =>
      this.coordinateMapper.updateScrollOffset(),
    );

    // Fullscreen and pointer lock
    addListener(document, "pointerlockchange", () =>
      this.handlePointerLockChange(),
    );

    addListener(document, "fullscreenchange", () =>
      this.handleFullscreenChange(),
    );

    console.log("Global event listener setup");
  }

  private setupScrollListeners(): void {
    this.eventManager.addContextListener(
      this.videoElement,
      "wheel",
      (event: Event) => {
        if (!this.shouldPoll) return;
        this.scrollState.handleScrollEvent(
          event as WheelEvent,
          this.mouseState,
          this.sendMessage,
        );
      },
    );
  }

  private setupKeyboardListeners(): void {
    this.eventManager.addContextListener(
      window,
      "keydown",
      (event: Event) => this.handleKeyboardEvent(event as KeyboardEvent),
      { capture: true },
    );
    this.eventManager.addContextListener(
      window,
      "keyup",
      (event: Event) => this.handleKeyboardEvent(event as KeyboardEvent),
      { capture: true },
    );
    this.eventManager.addContextListener(
      this.videoElement,
      "contextmenu",
      (event: Event) => event.preventDefault(),
    );
  }

  private setupTouchDeviceInputs(): void {
    this.setupTouchListeners();
    this.setupMouseListeners(); // Handles gamepad-simulated mouse events
    const shouldSimulateMouse = this.mouseSimulator.shouldSimulateMouseEvents();
    if (shouldSimulateMouse) {
      this.enableRemotePointer(); // Touch devices need visible cursor
    }
  }

  private setupPointerInputListeners(): void {
    if (DeviceDetector.isTouchDevice()) {
      this.setupTouchDeviceInputs();
    } else {
      this.setupMouseListeners();
      console.log("Set up input listener for desktop");
    }
  }

  private setupTouchListeners(): void {
    this.eventManager.addContextListener(
      this.videoElement,
      "touchstart",
      (event: Event) => this.handleTouchEvent(event as TouchEvent),
    );
    this.eventManager.addContextListener(
      this.videoElement,
      "touchend",
      (event: Event) => this.handleTouchEvent(event as TouchEvent),
    );
    this.eventManager.addContextListener(
      this.videoElement,
      "touchmove",
      (event: Event) => this.handleTouchEvent(event as TouchEvent),
    );
  }

  private setupMouseListeners(): void {
    this.eventManager.addContextListener(
      this.videoElement,
      "mousemove",
      (event: Event) => this.handleMouseEvent(event as MouseEvent),
    );
    this.eventManager.addContextListener(
      this.videoElement,
      "mousedown",
      (event: Event) => this.handleMouseEvent(event as MouseEvent),
    );
    this.eventManager.addContextListener(
      this.videoElement,
      "mouseup",
      (event: Event) => this.handleMouseEvent(event as MouseEvent),
    );
  }

  // ==================== INPUT HANDLERS ====================

  private handleMouseEvent(event: MouseEvent): void {
    if (!this.shouldPoll) return;

    const eventType = event.type;

    // Skip mouse moves if coordinate mapping not ready
    if (eventType === "mousemove" && !this.coordinateMapper.isReady()) {
      console.warn("CoordinateMapper is not ready yet");
      return;
    }

    // Handle pointer lock requests
    this.handlePointerLockRequests();

    // Check for pointer lock hotkey (Ctrl+Shift+Left Click)
    if (HotkeyDetector.isPointerLockHotkey(event)) {
      this.requestPointerLock();
      return;
    }

    // Sync mouse simulator position from real mouse events
    // so gamepad deltas are calculated from the correct starting point
    if (!this.isSyntheticMouseEvent(event)) {
      const rect = this.videoElement.getBoundingClientRect();
      this.mouseSimulator.syncPositionFromRealMouse(
        event.clientX - rect.left,
        event.clientY - rect.top,
      );
    }

    // Update mouse position
    this.updateMousePosition(event);

    // Update button state
    if (eventType === "mousedown" || eventType === "mouseup") {
      this.mouseState.updateButtonState(
        event.button,
        eventType === "mousedown",
      );
    }

    // Send mouse message
    this.sendMouseMessage();
    event.preventDefault();
  }

  private handleTouchEvent(event: TouchEvent): void {
    this.touchState.handleTouchEvent(
      event,
      this.coordinateMapper,
      this.sendMessage,
    );
  }

  // X11 keysyms for navigation keys that arrive via window keydown on TVs.
  private static readonly NAVIGATION_KEYSYMS: Record<string, number> = {
    ArrowLeft: 0xff51,
    ArrowUp: 0xff52,
    ArrowRight: 0xff53,
    ArrowDown: 0xff54,
    Enter: 0xff0d,
    Escape: 0xff1b,
    Tab: 0xff09,
    Home: 0xff50,
    End: 0xff57,
    PageUp: 0xff55,
    PageDown: 0xff56,
  };

  private setupGamepadHandler(): void {
    const previousAxes: { [key: number]: number } = { 0: 0, 1: 0 };
    const manager = GamepadPollingManager.getInstance();

    const btnId = manager.addEventListener(
      "button",
      (event: GamepadButtonEvent) => {
        if (!this.shouldPoll) return;
        if (this.mouseSimulator.shouldSimulateMouseEvents()) {
          // Button A = left click
          if (event.buttonIndex === 0) {
            this.mouseSimulator.simulateMouseEvent(
              event.value > 0 ? "mousedown" : "mouseup",
              "left",
            );
            // Button B = right click
          } else if (event.buttonIndex === 1) {
            this.mouseSimulator.simulateMouseEvent(
              event.value > 0 ? "mousedown" : "mouseup",
              "right",
            );
          }
          return;
        }
        this.sendMessage(
          `js,b,${event.enhanced.mappedIndex},${event.buttonIndex},${event.value}`,
        );
      },
      { priority: 100 },
    );

    const axisId = manager.addEventListener(
      "axis",
      (event: GamepadAxisEvent) => {
        if (!this.shouldPoll) return;
        if (this.mouseSimulator.shouldSimulateMouseEvents()) {
          if (event.axisIndex === 0 || event.axisIndex === 1) {
            previousAxes[event.axisIndex] = event.value;
            this.mouseSimulator.simulateMouseMovement(
              previousAxes[0],
              previousAxes[1],
            );
            return;
          }
        }
        this.sendMessage(
          `js,a,${event.enhanced.mappedIndex},${event.axisIndex},${event.value}`,
        );
      },
      { priority: 100 },
    );

    const connectId = manager.addEventListener(
      "connect",
      (event: GamepadConnectionEvent) => {
        const { gamepad, mappedIndex } = event.enhanced;
        console.log(
          `[gamepad] - connected: mapped index ${mappedIndex} (${event.enhanced.deviceType})`,
        );
        this.sendMessage(
          `js,c,${mappedIndex},${btoa(gamepad.id)},${gamepad.axes.length},${
            gamepad.buttons.length
          }`,
        );
        this.callbacks.onGamepadConnected?.(gamepad.id);
        if (this.mouseSimulator.shouldSimulateMouseEvents()) {
          this.enableRemotePointer();
        }
      },
      { priority: 100 },
    );

    const disconnectId = manager.addEventListener(
      "disconnect",
      (event: GamepadConnectionEvent) => {
        const { mappedIndex } = event.enhanced;
        console.log(`[gamepad] - disconnected: mapped index ${mappedIndex}`);
        this.sendMessage(`js,d,${mappedIndex}`);
        this.callbacks.onGamepadDisconnected?.();
      },
      { priority: 100 },
    );

    manager.startPolling();
    this.gamepadHandler = {
      listenerIds: [btnId, axisId, connectId, disconnectId],
    };
  }

  private handleKeyboardEvent(event: KeyboardEvent): void {
    // event.stopPropagation();
    // Block problematic browser shortcuts
    if (HotkeyDetector.shouldBlockShortcut(event)) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    // Handle menu hotkey (Ctrl+Shift+M)
    if (HotkeyDetector.isMenuHotkey(event)) {
      if (!document.fullscreenElement && this.callbacks.onMenuHotkey) {
        event.stopPropagation();
        this.callbacks.onMenuHotkey();
        event.preventDefault();
      }
      return;
    }

    // Handle fullscreen hotkey (Ctrl+Shift+F)
    if (HotkeyDetector.isFullscreenHotkey(event)) {
      if (!document.fullscreenElement && this.callbacks.onFullscreenHotkey) {
        event.stopPropagation();
        this.callbacks.onFullscreenHotkey();
        event.preventDefault();
      }
    }

    // Handle close tab hotkey (Ctrl+W on Windows/Cmd+W on Mac)
    if (HotkeyDetector.isCloseTabHotkey(event)) {
      if (document.fullscreenElement) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }
    }

    if (!this.shouldPoll) return;

    // In TV / native-keyboard mode, Guacamole is not attached to window so
    // navigation keys must be forwarded manually. Printable characters are
    // handled separately via the input event on keyboardInput.
    if (this.useNativeKeyboardInput) {
      const keysym = InputHandler.NAVIGATION_KEYSYMS[event.key];
      if (keysym) {
        const msg = event.type === "keydown" ? "kd" : "ku";
        this.sendMessage(`${msg},${keysym}`);
      }
    }
  }

  startGamepadPolling(): void {
    console.log("[gamepad] - starting gamepad polling");
    if (!this.gamepadHandler) {
      this.setupGamepadHandler();
    }
    GamepadPollingManager.getInstance().startPolling();
  }

  stopGamepadPolling(): void {
    console.log("[gamepad] - stopping gamepad polling");
    // Don't stop the singleton — just remove listeners so other consumers keep running
    if (this.gamepadHandler) {
      const manager = GamepadPollingManager.getInstance();
      this.gamepadHandler.listenerIds.forEach((id) =>
        manager.removeEventListener(id),
      );
      this.gamepadHandler = null;
    }
  }

  toggleMouseSimulation(): void {
    this.mouseSimulator.toggleMouseSimulation();

    if (this.mouseSimulator.shouldShowRemotePointer()) {
      this.enableRemotePointer();
    } else {
      this.hideRemotePointer();
    }
  }

  // ==================== POINTER LOCK & FULLSCREEN ====================

  private handlePointerLockRequests(): void {
    // Skip pointer lock requests on TV - TVs don't support pointer lock
    if (this.isTV) return;

    if (!document.pointerLockElement && this.features.relativeMouseMode) {
      this.requestPointerLock();
    }
  }

  private async requestPointerLock(): Promise<void> {
    if (!this.features.canLockInputs) {
      console.log("Pointer lock skipped: input locking not enabled");
      return;
    }

    requestPointerLock({ element: this.videoElement });
  }

  private exitPointerLock(): void {
    exitPointerLock();
    this.hideRemotePointer();
    console.log("Remote pointer visibility: False");
  }

  private handlePointerLockChange(): void {
    const isLocked = document.pointerLockElement !== null;

    this.sendMessage(`p,${isLocked ? 1 : 0}`);
    console.log(`Remote pointer visibility: ${isLocked ? "True" : "False"}`);
  }

  private handleFullscreenChange(): void {
    if (document.fullscreenElement) {
      // Request pointer lock when entering fullscreen
      if (!document.pointerLockElement) {
        this.requestPointerLock();
      }
      this.requestKeyboardLock();
    }
    // Reset keyboard state
    this.keyboardHandler?.reset();
    this.sendMessage("kr"); // Reset server-side keys
    this.callbacks.onFullscreenChange?.();
  }

  private requestKeyboardLock(): void {
    if (!this.features.canLockInputs) {
      console.log("Keyboard lock skipped: input locking not enabled");
      return;
    }

    requestKeyboardLock();
  }

  // ==================== GAMEPAD VIBRATION ====================

  /**
   * Handle rumble/vibration message from server
   */
  async handleRumble(msg: Record<string, any>): Promise<string | undefined> {
    if (!msg.data) {
      console.warn("[rumble] missing msg.data payload");
      return;
    }

    const { index: mappedIndex, low: rawLow, high: rawHigh } = msg.data;
    const isStop = rawHigh === 0 && rawLow === 0;

    const gamepad =
      GamepadPollingManager.getInstance().getGamepadObjectByMappedIndex(
        mappedIndex,
      );
    if (!gamepad) {
      console.warn(
        `[rumble] no gamepad found for mapped index: ${mappedIndex}`,
      );
      return;
    }

    return this.vibrateGamepad(gamepad, isStop)
      ? "Web: Gamepad API"
      : undefined;
  }

  private vibrateGamepad(gamepad: Gamepad, isStop = false): boolean {
    // @ts-ignore
    const actuator = gamepad.hapticActuators?.[0] ?? gamepad.vibrationActuator;

    if (!actuator) {
      console.warn(
        `[rumble] no haptic actuator available for gamepad at browser index: ${gamepad.index}`,
      );
      return false;
    }

    if (typeof actuator.playEffect !== "function") {
      console.warn("[rumble] actuator has no playEffect method");
      return false;
    }

    const effect = {
      startDelay: 0,
      duration: isStop ? 0 : 400,
      weakMagnitude: isStop ? 0.0 : 1.0,
      strongMagnitude: isStop ? 0.0 : 1.0,
    };

    actuator
      .playEffect("dual-rumble", effect)
      .then((result: string) => {
        console.log("[rumble] playEffect resolved", {
          result,
          browserIndex: gamepad.index,
          isStop,
        });
      })
      .catch((error: unknown) => {
        console.error("[rumble] playEffect rejected", {
          error,
          browserIndex: gamepad.index,
          effect,
        });
      });
    return true;
  }

  /**
   * Test rumble on a gamepad by mapped index
   */
  runRumble(mappedIndex = 0): Promise<string | undefined> {
    return this.handleRumble({
      data: { index: mappedIndex, low: 32768, high: 16384 },
    });
  }

  // Rumble every connected gamepad directly
  runRumbleAll(): string | undefined {
    const enhanced =
      GamepadPollingManager.getInstance().getAllEnhancedGamepads();
    if (!enhanced.length) {
      console.log("[rumble] runRumbleAll: no gamepads connected");
      return undefined;
    }

    let rumbled = 0;
    for (const { gamepad } of enhanced) {
      if (this.vibrateGamepad(gamepad)) rumbled++;
    }
    return rumbled > 0 ? `Web: rumbled ${rumbled} gamepad(s)` : undefined;
  }

  // ==================== HELPER METHODS ====================

  private isSyntheticMouseEvent(
    event: MouseEvent,
  ): event is SyntheticMouseEvent {
    return (event as SyntheticMouseEvent).isSyntheticMouseEvent === true;
  }

  private updateMousePosition(event: MouseEvent): void {
    if (document.pointerLockElement) {
      // Relative movement mode
      const scaling = this.features.cursorScaling || 1;

      // For synthetic events (from gamepad), movementX/Y are read-only and default to 0
      // use syntheticMovementX/Y instead
      const movementX = this.isSyntheticMouseEvent(event)
        ? event.syntheticMovementX
        : event.movementX;
      const movementY = this.isSyntheticMouseEvent(event)
        ? event.syntheticMovementY
        : event.movementY;

      this.mouseState.setPosition(
        Math.trunc(movementX * scaling),
        Math.trunc(movementY * scaling),
      );
    } else if (event.type === "mousemove") {
      // Absolute position mode
      const coords = this.coordinateMapper.clientToVideo(
        event.clientX,
        event.clientY,
      );
      this.mouseState.setPosition(coords.x, coords.y);
    }
  }

  private sendMouseMessage(): void {
    // For TV using mouse simulator in gesture mode, send absolute positions
    // For pointer lock mode, send relative movements
    const useRelative = this.isTV ? false : !!document.pointerLockElement;
    const messageType = useRelative ? "m2" : "m";
    const message = `${messageType},${this.mouseState.x},${this.mouseState.y},${this.mouseState.buttonMask},0`;
    this.sendMessage(message);
  }

  private handleSyntheticMouseMove(
    movementX: number,
    movementY: number,
    clientX: number,
    clientY: number,
  ): void {
    if (!this.coordinateMapper.isReady()) return;
    this.handlePointerLockRequests();

    if (!this.isTV && document.pointerLockElement) {
      const scaling = this.features.cursorScaling || 1;
      this.mouseState.setPosition(
        Math.trunc(movementX * scaling),
        Math.trunc(movementY * scaling),
      );
    } else {
      const coords = this.coordinateMapper.clientToVideo(clientX, clientY);
      this.mouseState.setPosition(coords.x, coords.y);
    }

    this.sendMouseMessage();
  }

  private enableRemotePointer(): void {
    console.log("Enabling remote pointer");
    this.sendMessage("p,1");
  }

  private hideRemotePointer(): void {
    console.log("Hide remote pointer");
    this.sendMessage("p,0");
  }

  private initializeKeyboardHandler(): void {
    // WebOS fallback: onkeydown does not fire on window, so listen to the
    // hidden keyboard-input element that receives virtual keyboard input.
    const keyboardInput = this.isTV
      ? (document.getElementById(
          EON_KEYBOARD_INPUT_ID,
        ) as HTMLInputElement | null)
      : null;

    if (keyboardInput) {
      this.useNativeKeyboardInput = true;

      // Attach Guacamole to keyboardInput so it captures keydown/keyup on the
      // focused element. On TVs where keydown fires on focused elements (but not
      // on window), Guacamole gets full modifier info — "!" → Shift+1.
      this.keyboardHandler = new Keyboard(keyboardInput, this.isTV);
      // this.keyboardHandler.setDebugMode(true);
      this.keyboardHandler.onkeydown = (keysym: number) => {
        if (this.isMenuOpen) return;
        this.sendMessage(`kd,${keysym}`);
      };
      this.keyboardHandler.onkeyup = (keysym: number) => {
        if (this.isMenuOpen) return;
        this.sendMessage(`ku,${keysym}`);
      };
      this.keyboardHandler.onunicode = (char: string) => {
        if (this.isMenuOpen) return;
        const bytes = new TextEncoder().encode(char);
        const base64 = btoa(
          Array.from(bytes, (b) => String.fromCodePoint(b)).join(""),
        );
        this.sendMessage(`cw,${base64}`);
        this.sendMessage(`kd,${0xffe3}`);
        this.sendMessage(`kd,${0x76}`);
        this.sendMessage(`ku,${0x76}`);
        this.sendMessage(`ku,${0xffe3}`);
      };

      // Fallback for TVs where keydown does not fire at all: track input value
      // changes and synthesise keystrokes from the diff. A flag prevents
      // double-sending on TVs where both keydown and input fire.
      //
      // Keep a single placeholder character so the input is never fully empty.
      // This allows backspace to always produce a value-change event, even when
      // the user keeps deleting past the last visible character.
      const PLACEHOLDER = " ";
      keyboardInput.value = PLACEHOLDER;
      let previousValue = PLACEHOLDER;
      let keydownFired = false;

      this.eventManager.addContextListener(keyboardInput, "keydown", () => {
        // keydownFired = true;
      });

      this.eventManager.addContextListener(keyboardInput, "input", () => {
        const currentValue = keyboardInput.value;

        if (keydownFired) {
          // Guacamole already handled this keystroke via keydown — just sync
          // the tracked value so the next diff is correct.
          keydownFired = false;
          previousValue = currentValue;

          // Replenish the placeholder if the input got emptied.
          if (currentValue.length === 0) {
            keyboardInput.value = PLACEHOLDER;
            previousValue = PLACEHOLDER;
          }
          return;
        }

        // keydown did not fire: synthesise keystrokes from the value diff.
        if (currentValue.length > previousValue.length) {
          const added = currentValue.slice(previousValue.length);
          this.keyboardHandler?.type(added);
        } else if (currentValue.length < previousValue.length) {
          const deleteCount = previousValue.length - currentValue.length;
          for (let i = 0; i < deleteCount; i++) {
            this.keyboardHandler?.press(0xff08);
            this.keyboardHandler?.release(0xff08);
          }
        }

        // Replenish the placeholder so the next backspace still fires an event.
        if (currentValue.length === 0) {
          keyboardInput.value = PLACEHOLDER;
          previousValue = PLACEHOLDER;
        } else {
          previousValue = currentValue;
        }
      });
    } else {
      // Normal path: Guacamole listens to window keydown/keyup directly.
      this.keyboardHandler = new Keyboard(window, this.isTV);
      // this.keyboardHandler.setDebugMode(true);
      this.keyboardHandler.onkeydown = (keysym: number) => {
        if (this.isMenuOpen) return;
        this.sendMessage(`kd,${keysym}`);
      };
      this.keyboardHandler.onkeyup = (keysym: number) => {
        if (this.isMenuOpen) return;
        this.sendMessage(`ku,${keysym}`);
      };
      this.keyboardHandler.onunicode = (char: string) => {
        if (this.isMenuOpen) return;
        const bytes = new TextEncoder().encode(char);
        const base64 = btoa(
          Array.from(bytes, (b) => String.fromCodePoint(b)).join(""),
        );
        this.sendMessage(`cw,${base64}`);
        this.sendMessage(`kd,${0xffe3}`);
        this.sendMessage(`kd,${0x76}`);
        this.sendMessage(`ku,${0x76}`);
        this.sendMessage(`ku,${0xffe3}`);
      };
      this.keyboardHandler.setupEventListeners();
    }
  }

  private cleanupKeyboardHandler(): void {
    if (!this.keyboardHandler) return;

    this.keyboardHandler.onkeydown = null;
    this.keyboardHandler.onkeyup = null;
    this.keyboardHandler.destroy();
    this.keyboardHandler = null;
    this.sendMessage("kr");
  }

  private getClientResolution(): ClientResolution {
    const adjustDimension = (value: number): number => value - (value % 2);
    const width = adjustDimension(
      document.body.offsetWidth * window.devicePixelRatio,
    );
    const height = adjustDimension(
      document.body.offsetHeight * window.devicePixelRatio,
    );

    return {
      width: parseInt(width.toString()),
      height: parseInt(height.toString()),
    };
  }

  // ==================== LEGACY COMPATIBILITY ====================

  // Keep original property names and methods for backward compatibility
  get element(): HTMLVideoElement {
    return this.videoElement;
  }
  get send(): (message: string) => void {
    return this.sendMessage;
  }
  get mouseRelative(): boolean {
    return this.features.relativeMouseMode;
  }
  set mouseRelative(value: boolean) {
    this.features.relativeMouseMode = value;
  }
  get m(): CoordinateMapping | null {
    return this.coordinateMapper.mapping;
  }
  get buttonMask(): number {
    return this.mouseState.buttonMask;
  }
  get x(): number {
    return this.mouseState.x;
  }
  get y(): number {
    return this.mouseState.y;
  }
  get cursorScaleFactor(): number | null {
    return this.features.cursorScaling;
  }
  get onmenuhotkey(): (() => void) | null {
    return this.callbacks.onMenuHotkey;
  }
  set onmenuhotkey(value: (() => void) | null) {
    this.callbacks.onMenuHotkey = value;
  }
  get onfullscreenhotkey(): (() => void) | null {
    return this.callbacks.onFullscreenHotkey;
  }
  set onfullscreenhotkey(value: (() => void) | null) {
    this.callbacks.onFullscreenHotkey = value;
  }
  get onfullscreenchange(): (() => void) | null {
    return this.callbacks.onFullscreenChange;
  }
  set onfullscreenchange(value: (() => void) | null) {
    this.callbacks.onFullscreenChange = value;
  }
  get ongamepadconnected(): ((gamepadId: string) => void) | null {
    return this.callbacks.onGamepadConnected;
  }
  set ongamepadconnected(value: ((gamepadId: string) => void) | null) {
    this.callbacks.onGamepadConnected = value;
  }
  get ongamepaddisconneceted(): (() => void) | null {
    return this.callbacks.onGamepadDisconnected;
  }
  set ongamepaddisconneceted(value: (() => void) | null) {
    this.callbacks.onGamepadDisconnected = value;
  }
  get onresizeend(): (() => void) | null {
    return this.callbacks.onResizeEnd;
  }
  set onresizeend(value: (() => void) | null) {
    this.callbacks.onResizeEnd = value;
  }
  get keyboard(): Keyboard | null {
    return this.keyboardHandler;
  }
  get gamepadManager(): GamepadPollingManager | null {
    return this.gamepadHandler ? GamepadPollingManager.getInstance() : null;
  }

  getCursorScaleFactor(options?: CursorScalingOptions): void {
    this.calculateCursorScaling(options);
    console.log("Updated cursor scaling: ", this.features.cursorScaling);
  }
  attach_context(): void {
    this.attachInputContext();
  }
  detach_context(): void {
    this.detachInputContext();
  }
  getWindowResolution(): [number, number] {
    const res = this.getClientResolution();
    return [res.width, res.height];
  }
}

export default InputHandler;
export type {
  ClientResolution,
  InputCallbacks,
  InputFeatures,
  CursorScalingOptions,
};

export type {
  CoordinateMapping,
  VideoCoordinates,
} from "../input/CoordinateMapper";
